
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'offices-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const thumbnail = ref('/media/avatars/blank-image.jpg');
      let file;
      const badges = ref<Array<any>>([]);
      const selectedBadgeId = ref<string | number>('');

      const selectedBadge = computed(() => {
        return badges.value.find((badge) => badge.id === selectedBadgeId.value);
      });

      const previewImage = (e) => {
        file = e.target.files[0];
        thumbnail.value = URL.createObjectURL(file);
      };

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const officeSchema = Yup.object().shape({
        name: Yup.string().label('English Name'),
        email: Yup.string()
          .email(() => translate('EMAIL_NOT_VALID'))
          .label('Email'),
      });

      const onSubmitCreate = async (values) => {
        delete values.logo;
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        if (selectedBadgeId.value) {
          delete values.badges;
          values.badgeId = selectedBadgeId.value;
        }
        const createOffice = await store.dispatch(
          Actions.CREATE_OFFICE,
          values
        );
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        const logoFormData = new FormData();
        // eslint-disable-next-line
        logoFormData.append('logo', file);

        if (logoFormData) {
          await store.dispatch(Actions.UPLOAD_OFFICE_LOGO, {
            data: logoFormData,
            id: createOffice.id,
          });
        }

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_OFFICE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'offices-listing' });
          });
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('officesCreating'), [
          translate('offices'),
        ]);

        try {
          badges.value = await store.dispatch(Actions.GET_ALL_BADGES);
        } catch (error) {
          console.error('Error fetching badges:', error);
        }
      });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitCreate,
        submitButton,
        translate,
        officeSchema,
        goBack,
        can,
        previewImage,
        thumbnail,
        file,
        badges,
        selectedBadgeId,
        selectedBadge,
      };
    },
  });
